<template>
  <div v-if="moduleData" class="container-fluid section-p70">
    <div class="row">
      <div class="col-auto title-blue">
        {{ t("MANAGE_MODULE") }} - {{ moduleData.code }}
      </div>

      <div class="col align-self-center">
        <Button
            :label="isModuleArchived ? t('ARCHIVED') : t('ACTIVE')"
            :severity="isModuleArchived ? 'danger' : 'primary'"
            v-tooltip="isAdmin() ? (isModuleArchived ? t('UNARCHIVE') : t('ARCHIVE')) : ''"
            :disabled="!isAdmin()"
            @click="toggleModuleArchive()"
        />
      </div>
    </div>

    <div class="row mt-3">
      <div class="col-xl-3">
        <Card class="manage-module-base-card">
          <template #title>{{ t("BASE_DATA") }}</template>

          <template #content>
            <form id="moduleForm" ref="form">
              <div class="mb-2">
                <label for="code" class="form-label">{{ t('MODULE_CODE') }}</label>
                <InputText
                    id="code"
                    :model-value="moduleData.code"
                    disabled
                    @update:model-value="moduleData.code = formatCode($event)"
                    class="w-100"
                />
              </div>

              <div class="mb-2">
                <label for="type" class="form-label">{{ t('MODULE_TYPE') }}</label>
                <InputText
                    id="type"
                    :model-value="t(moduleData.type)"
                    disabled
                    class="w-100"
                />
              </div>

              <div class="mb-2">
                <label for="rvk" class="form-label">{{ t('INTERNATIONAL_PROTECTION') }}</label>
                <Checkbox input-id="rvk" v-model="moduleData.rvk" disabled binary/>
              </div>

              <div class="mb-2">
                <label for="temp" class="form-label">{{ t('TEMPORARY_PROTECTION') }}</label>
                <Checkbox input-id="temp" v-model="moduleData.temp" disabled binary/>
              </div>

              <div class="mb-2">
                <label for="rvkt" class="form-label">{{ t('INTERNATIONAL_PROTECTION_APPLICANT') }}</label>
                <Checkbox input-id="rvkt" v-model="moduleData.rvkt" disabled binary/>
              </div>

              <div class="form-label mb-2">{{ t("TRANSLATIONS") }}</div>

              <div class="p-2">
                <div v-for="locale in $i18n.availableLocales" :key="locale" class="mb-2">
                  <label :for="'locale-' + locale" class="form-label">{{ locale }}</label>
                  <InputText
                      :id="'locale-' + locale"
                      v-model="moduleData.translations[locale]"
                      :disabled="isModuleArchived"
                      required
                      class="w-100"
                  />
                </div>
              </div>

              <div class="mb-2">
                <label for="edit-max-attempts" class="form-label">{{ t('MAX_ATTEMPTS') }}</label>
                <InputNumber id="edit-max-attempts"
                             :showButtons="true"
                             :placeholder="t('UNLIMITED')"
                             :disabled="isModuleArchived || moduleData.isLanguageTraining"
                             v-model="moduleData.maxAttempts"
                             inputClass="w-100"
                             min="0"
                             class="d-block"/>
              </div>

              <div class="mb-2">
                <label for="edit-max-successful-passes" class="form-label">{{ t('MAX_SUCCESSFUL_PASSES') }}</label>
                <InputNumber id="edit-max-successful-passes"
                             :showButtons="true"
                             :placeholder="t('UNLIMITED')"
                             :disabled="isModuleArchived || moduleData.isLanguageTraining"
                             v-model="moduleData.maxSuccessfulPasses"
                             inputClass="w-100"
                             min="0"
                             class="d-block"/>
              </div>
            </form>

            <Button
                v-if="isAdmin()"
                :label="t('SAVE')"
                :disabled="isModuleArchived"
                class="w-100 mt-2"
                @click="updateData"
            />
          </template>
        </Card>
      </div>

      <div class="col-xl-9">
        <Tabs value="0">
          <TabList>
            <Tab value="0">{{ t('TRAINING_GROUPS') }}</Tab>
            <Tab value="1">{{ t('TRAINER') }}</Tab>
            <Tab value="2" v-if="isAdmin()">{{ t('RECOMMY') }}</Tab>
          </TabList>

          <TabPanels>
            <TabPanel value="0">
              <div class="row">
                <div class="col text-end">
                  <Button
                      v-if="isGroupAddButtonVisible"
                      :label="t('ADD')"
                      :disabled="isModuleArchived"
                      @click="showGroupCreationModal"
                  />
                </div>
              </div>

              <div class="row">
                <div class="col">
                  <PaginatedTable id="paginated-moduleGroups"
                                  :fields="fields"
                                  :on-page-change="getGroups"
                                  :small="true"
                                  ref="groupsTableRef"
                  >
                    <template v-slot:cell(language)="{item}">
                      <span>{{ getGroupLangOptions(t).find(v => v.value === item.language)?.text ?? item.language }}</span>
                    </template>
                    <template v-slot:cell(action)="{ item }">
                      <div class="align-items-center">
                        <router-link :to="{ name: 'ManageGroup', params: { id: `${item.id}` } }">
                          <Button :label="t('VIEW')"/>
                        </router-link>
                      </div>
                    </template>
                  </PaginatedTable>
                </div>
              </div>
            </TabPanel>

            <TabPanel value="1">
              <TrainerTab
                  tabTitle="TRAINER"
                  :trainers="trainers"
                  :isArchived="isModuleArchived"
                  :trainerFields="trainerFields"
                  :allowedToEdit="canManageModuleTrainers()"
                  @removeTrainer="removeTrainer"
                  @submitTrainer="submitTrainer"
              />
            </TabPanel>

            <TabPanel v-if="isAdmin()" value="2">
              <RecommyTab
                  :trainingId="route.params.id"
                  :isArchived="isModuleArchived"
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </div>

    <group-creation-modal
        :module="moduleData"
        :trainers="trainers"
        @added-group="resetGroupsTable"
        :is-admin="isAdmin()"
    ></group-creation-modal>
  </div>
</template>

<script setup>
import EventBus from "@/util/eventBus";
import groupCreationModal from "@/views/components/modals/groupCreationModal.vue";
import TrainerTab from "@/views/components/TrainerTab.vue";
import RecommyTab from "@/views/components/RecommyTab.vue";
import {computed, onBeforeMount, ref} from "vue";
import {injectAuth, injectAxios} from "@/util/inject";
import {useRoute} from "vue-router";
import {useI18n} from "vue-i18n";
import {useDateFormat} from "@/mixins/dateFormatMixin2.ts";
import PaginatedTable from "@/views/components/PaginatedTable.vue";
import {useToast} from "@/util/toastUtils";
import {getGroupLangOptions} from "@/model/group";
import {Language} from "@/model/language";

const TRANSLATION_LOCALES = [Language.EST, Language.ENG, Language.RUS];

function normalizeModuleData(data) {
  if (!data) {
    return data;
  }
  const translations = {...data.translations};
  for (const loc of TRANSLATION_LOCALES) {
    if (translations[loc] == null) {
      translations[loc] = "";
    }
  }
  return {...data, translations};
}

const props = defineProps(["module", "school"]);
const axios = injectAxios();
const route = useRoute();
const {t, locale} = useI18n();
const toast = useToast();
const auth = injectAuth();
const {dateFormat} = useDateFormat();
const moduleData = ref(null);
const trainers = ref(null);
const isGroupAddButtonVisible = ref(false);
const groupsTableRef = ref(null);

onBeforeMount(() => {
  getTrainers();
  getModule();
  checkCanCreateGroup();
});

const trainerFields = computed(() => {
  return [
    {key: "fullName", label: t("NAME"), sortable: true},
    {key: "ssn", label: t("SSN"), sortable: true},
    {key: "phone", label: t("PHONE"), sortable: true},
    {key: "email", label: t("EMAIL"), sortable: true},
  ];
});

const showGroupCreationModal = () => {
  EventBus.$emit("show-group-creation-modal");
};

const fields = computed(() => {
  return [
    {key: "businessName", label: t("SCHOOL"), sortable: true},
    {key: "title", label: t("GROUP_TITLE"), sortable: true},
    {key: "startEvent", label: t("START"), sortable: true, formatter: dateFormat},
    {key: "endEvent", label: t("END"), sortable: true, formatter: dateFormat},
    {key: "language", label: t("LANGUAGE"), sortable: true},
    {key: "location", label: t("LOCATION"), sortable: true},
    {key: "groupSize", label: t("OCCUPANCY"), sortable: true, formatter: getGroupSize},
    {key: "status", label: t("STATUS"), sortable: true, formatter: getTranslatedStatus},
    {key: "action", label: "", sortable: false},
  ];
});

const isAdmin = () => {
  return auth.isAuthenticated && auth.hasRole("ADMIN");
};

const canManageModuleTrainers = () => {
  return isAdmin() || (auth.isRvktAdmin() && Boolean(moduleData.value?.rvkt));
};

const isModuleArchived = computed(() => {
  return moduleData.value?.archived;
});

async function checkCanCreateGroup() {
  const resp = await axios.get(`/training/${route.params.id}/canCreateGroup`);
  isGroupAddButtonVisible.value = resp.data.result;
}

const getModule = async () => {
  const resp = await axios.get(`/training/${route.params.id}`);
  moduleData.value = normalizeModuleData(resp.data);
};

const isSchoolManager = () => {
  return auth.isAuthenticated && auth.hasRole("SCHOOL_MANAGER");
};

const schoolAdminEndpoint = `/school/${route.params.id}/school_trainers`
const adminEndpoint = `/training/${route.params.id}/trainers`

const teachersEndpoint = computed(() => {
  if (isSchoolManager()) {
    return schoolAdminEndpoint
  } else {
    return adminEndpoint
  }
})

const getTrainers = async () => {
  const response = await axios.get(
      teachersEndpoint.value,
  );
  trainers.value = response.data;
  return trainers.value;
};

const getGroups = async (params) => {
  params["currentLocale"] = locale.value;
  params["moduleId"] = route.params.id;

  return axios.get(`/training/groups/search`, {params: params});
};

async function toggleModuleArchive() {
  const response = await axios.post(
      `/training/${moduleData.value.id}/archive`,
  );
  moduleData.value = normalizeModuleData(response.data);
}

const getGroupSize = (value, key, item) => {
  return (
      (item.numberOfTrainees ? item.numberOfTrainees : 0) + " / " + item.groupSize
  );
};

const getTranslatedStatus = (value) => {
  return t(value);
};

async function submitTrainer(trainer) {
  const response = await axios.post(
      `/training/${route.params.id}/trainer/${trainer.value}`,
  );
  toast.makeSuccessToast(t("SAVED"));
  trainers.value.push(response.data);
  await getTrainers()
}

async function removeTrainer(id) {
  await axios.delete(
      `/training/${route.params.id}/trainer/${id}`,
  );
  toast.makeSuccessToast(t("REMOVED"));
  await getTrainers();
}

async function updateData() {
  const formData = {
    ...moduleData.value,
  };

  if (formData.hasOwnProperty("maxAttempts") && !formData.maxAttempts) {
    delete formData["maxAttempts"];
  }

  if (formData.hasOwnProperty("maxSuccessfulPasses") && !formData.maxSuccessfulPasses) {
    delete formData["maxSuccessfulPasses"];
  }

  const response = await axios.patch(
      `/training/${route.params.id}`,
      formData,
  );
  toast.makeSuccessToast(t("SAVED"));
  moduleData.value = normalizeModuleData(response.data);

}

function resetGroupsTable() {
  groupsTableRef.value.reset();
}

const formatCode = (value) => {
  return value.toUpperCase().replace(" ", "_");
};
</script>


<style lang="less" scoped>
.table-header-btn {
  margin-right: 2%;
  height: 30px;
}

.manage-module-base-card {
  text-align: left;
}

.red {
  color: red;
}

.add-me {
  background-color: white;
  border-top: 0;
  border-left: 0;
  border-right: 0;
}
</style>
